import { isInAppPath } from './pushPayload';

/**
 * Pure route derivation for a tapped push notification.
 *
 * Two producers share this handler, and they carry different payloads:
 *
 *  - **Chat** (the unified doorbell, C5a.3-unify) sends `data {kind: "chat",
 *    conversation_id, up_to_seq}`. The route derives from `conversation_id` ONLY —
 *    a tap opens the conversation and opening always syncs, so `up_to_seq` is
 *    deliberately not a dedup gate on this path.
 *  - **Notifications** (`user_notifications`, S4/S5) send `data {kind, source_id,
 *    node_id, delivery_id, engagement_url, landing_url}` — `kind` being one of the
 *    server's channel kinds (`health_change`, `diagnostic_create`, `report_create`, …),
 *    and `landing_url` the in-app destination the SERVER composed. The server owns that
 *    destination because not every kind lands on the feed; the client only validates it.
 *
 * `landing_url` carries the feed drawer's grammar when the notification has a card to
 * open — `/dashboard/feed/<node>?asset=<node>&event=<type>:<id>&tab=history`, parsed by
 * dashboards/feed/drawerUrl.ts — and is absent for a notification with no single node to
 * land on at all.
 *
 * `node_id` is the fallback chain's floor (notification-experience.md §3): a push whose
 * `landing_url` is missing or malformed still lands on that node rather than nowhere,
 * and an id outside the user's tree is bounced to their root node downstream
 * (src/main/platform/index.tsx). A tap that resolves to nothing is worse than a tap that
 * resolves to something approximate.
 *
 * A chat tap lands on `/dashboard/chat?conversation=<id>`: the chat page settles the node the user
 * is at and opens the conversation there, or in its node's Chat tab when it stands on one.
 *
 * Kept as a framework-free pure function so it is unit-testable without the
 * Capacitor push lifecycle.
 */

/** FCM data values are always strings; node ids are positive (0 means "no tenant"). */
const asNodeId = (value: unknown): string | null =>
    typeof value === 'string' && /^[1-9]\d*$/.test(value) ? value : null;

export const routeForNotification = (data: Record<string, unknown> | undefined): string | null => {
    if (!data) return null;

    // Chat doorbell: the route derives from conversation_id ONLY. A malformed chat
    // payload (missing or empty id) routes nowhere — it must not fall through to the
    // landing_url extra, which for chat would be a destination nobody composed.
    if (data.kind === 'chat') {
        if (typeof data.conversation_id === 'string' && data.conversation_id) {
            return `/dashboard/chat?conversation=${encodeURIComponent(data.conversation_id)}`;
        }
        return null;
    }

    if (isInAppPath(data.landing_url)) {
        return data.landing_url;
    }

    const nodeId = asNodeId(data.node_id);
    return nodeId ? `/dashboard/feed/${nodeId}` : null;
};
